/* ═══════════════════════════════════════════════════════════════════════════
   ALTUREX • Cart Drawer — Web Component propio sobre la AJAX Cart API.
   /cart.js · /cart/add.js · /cart/change.js · /cart/update.js
   Integración:
     · Intercepta el ícono del header (#cart-icon-bubble / [href="/cart"]).
     · Escucha los eventos que ya dispara el PDP custom: 'cart:open' y 'cart:refresh'.
     · Expone API compatible con product-form.js: open/close/renderContents/
       getSectionsToRender/setActiveElement (colección quick-add).
     · Actualiza el badge del header (.alturex-icon__badge) en tiempo real.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const CFG = window.alturexCartConfig || {};
  const THRESHOLD = CFG.threshold || 11099000; // $110.990 CLP, en "centavos" Shopify (×100)

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  // CLP sin decimales: 1999000 -> "$19.990"
  function money(cents) {
    const value = Math.round((cents || 0) / 100);
    return '$' + value.toLocaleString('es-CL');
  }

  function el(tag, cls, html) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (html != null) node.innerHTML = html;
    return node;
  }

  const ICON_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

  /* ── Badge del header (contador en tiempo real) ──────────────────────── */
  function updateHeaderBadge(count) {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    let badge = link.querySelector('.alturex-icon__badge');
    const n = parseInt(count, 10) || 0;
    if (n > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'alturex-icon__badge';
        link.appendChild(badge);
      }
      badge.textContent = n;
    } else if (badge) {
      badge.remove();
    }
  }
  window.updateCartCountBubble = updateHeaderBadge;

  /* ── Web Component ───────────────────────────────────────────────────── */
  class AlturexCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.panel = this.querySelector('.axd__panel');
      this.itemsEl = this.querySelector('[data-axd-items]');
      this.emptyEl = this.querySelector('[data-axd-empty]');
      this.shipEl = this.querySelector('[data-axd-ship]');
      this.promoEl = this.querySelector('[data-axd-promo]');
      this.promoNudgeEl = this.querySelector('[data-axd-promo-nudge]');
      this.subtotalEl = this.querySelector('[data-axd-subtotal]');
      this.countEl = this.querySelector('[data-axd-count]');
      this.footerEl = this.querySelector('[data-axd-footer]');
      this.crossEl = this.querySelector('[data-axd-cross]');
      this.errorEl = this.querySelector('[data-axd-error]');
      this.activeElement = null;
      this.busy = false;

      this.bindStaticEvents();
      this.interceptHeaderIcon();

      // Estado inicial desde el carrito real (sin abrir).
      this.getCart().then((cart) => { if (cart) this.render(cart); });

      window.alturexCart = this;
    }

    /* ---- Eventos estáticos (overlay, X, ESC, delegación, integración) ---- */
    bindStaticEvents() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-axd-close]')) { e.preventDefault(); this.close(); return; }

        const stepBtn = e.target.closest('[data-axd-step]');
        if (stepBtn) { this.onStep(stepBtn); return; }

        const removeBtn = e.target.closest('[data-axd-remove]');
        if (removeBtn) { this.onRemove(removeBtn); return; }

        const addBtn = e.target.closest('[data-axd-add]');
        if (addBtn) { this.onCrossAdd(addBtn); return; }

        const checkout = e.target.closest('[data-axd-checkout]');
        if (checkout) { e.preventDefault(); window.location.href = '/checkout'; return; }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Contrato del PDP custom (templates de producto). El PDP dispara cart:open
      // (sincrónico, el add aún no termina) y luego cart:refresh (add listo).
      // cart:open solo abre el shell con loading; cart:refresh trae el producto.
      document.documentElement.addEventListener('cart:open', () => {
        // El add aún no termina: marcamos espera y abrimos con loading.
        // El fetch real lo hará cart:refresh (cuando el producto ya está en el carrito).
        this._awaitingAdd = true;
        clearTimeout(this._awaitTimer);
        this._awaitTimer = setTimeout(() => { this._awaitingAdd = false; this.hideSyncing(); }, 6000);
        this._openShell();
        this.showSyncing();
      });
      window.addEventListener('cart:refresh', () => {
        this._awaitingAdd = false;
        clearTimeout(this._awaitTimer);
        this.refresh(true);
      });

      // Cambios en la página /cart (cart.js publica cartUpdate con item_count).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
          const data = ev && ev.cartData;
          if (data && typeof data.item_count !== 'undefined') updateHeaderBadge(data.item_count);
        });
      }
    }

    interceptHeaderIcon() {
      const icon = document.querySelector('#cart-icon-bubble') || document.querySelector('[href="/cart"]');
      if (!icon) return;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.addEventListener('click', (e) => { e.preventDefault(); this.open(icon); });
    }

    /* ---- Abrir / cerrar ---- */
    open(trigger, withLoading) {
      this._openShell(trigger);
      if (withLoading || this._awaitingAdd) this.showSyncing();
      // Si estamos esperando que termine un add (flujo del PDP), no hacemos fetch aquí:
      // cart:refresh traerá el carrito ya actualizado. En cualquier otro caso, refrescamos.
      if (!this._awaitingAdd) this.refresh();
    }

    // Abre solo la estructura (sin fetch). Usado por el evento cart:open del PDP,
    // donde el add aún no terminó: cart:refresh traerá el producto.
    _openShell(trigger) {
      if (trigger) this.activeElement = trigger;
      else if (!this.activeElement) this.activeElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('axd-locked');
      requestAnimationFrame(() => this.panel && this.panel.focus());
    }

    showSyncing() {
      if (this.panel) this.panel.classList.add('is-syncing');
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => this.hideSyncing(), 6000); // red de seguridad
    }
    hideSyncing() {
      clearTimeout(this._syncTimer);
      if (this.panel) this.panel.classList.remove('is-syncing');
    }

    close() {
      this.hideSyncing();
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('axd-locked');
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus();
      }
    }

    /* ---- API compatible con product-form.js (quick-add de colección) ---- */
    getSectionsToRender() { return []; }
    setActiveElement(node) { this.activeElement = node; }
    renderContents() { this.open(null, true); } // product-form ya hizo el add; abrimos con loading

    /* ---- AJAX Cart API ---- */
    getCart() {
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);
    }

    refresh(openAfter) {
      // Guard de secuencia: solo la última llamada puede pintar (evita que una
      // respuesta vieja —p.ej. el fetch pre-add— sobrescriba el carrito actualizado).
      const token = (this._refreshToken = (this._refreshToken || 0) + 1);
      return this.getCart().then((cart) => {
        if (token !== this._refreshToken) return cart; // respuesta fuera de orden: ignorar
        if (cart) this.render(cart);
        this.hideSyncing();
        if (openAfter && !this.classList.contains('is-open')) this._openShell();
        return cart;
      });
    }

    // Cambia la cantidad de una línea (line index 1-based). qty 0 = quitar.
    changeLine(line, qty) {
      if (this.busy) return Promise.resolve();
      this.setBusy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty }),
      })
        .then((r) => { if (!r.ok) throw new Error('change'); return r.json(); })
        .then((cart) => {
          this.render(cart);
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(() => this.showError('No pudimos actualizar el carrito. Intenta de nuevo.'))
        .finally(() => this.setBusy(false));
    }

    addVariant(id, qty) {
      if (this.busy) return Promise.resolve(false);
      this.setBusy(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(id, 10), quantity: qty || 1 }),
      })
        .then((r) => {
          if (r.status === 422) return r.json().then((d) => { throw new Error(d.description || 'Sin stock'); });
          if (!r.ok) throw new Error('add');
          return r.json();
        })
        .then(() => this.refresh())
        .then(() => true)
        .catch((err) => {
          this.showError(err.message === 'add' ? 'No pudimos agregar el producto. Revisa tu conexión.' : err.message);
          return false;
        })
        .finally(() => this.setBusy(false));
    }

    /* ---- Handlers de UI ---- */
    onStep(btn) {
      const line = parseInt(btn.dataset.line, 10);
      const next = parseInt(btn.dataset.next, 10);
      const row = btn.closest('.axd__item');
      if (row) row.classList.add('is-busy');
      this.changeLine(line, next);
    }

    onRemove(btn) {
      const line = parseInt(btn.dataset.line, 10);
      const row = btn.closest('.axd__item');
      if (row) row.classList.add('is-busy');
      this.changeLine(line, 0);
    }

    onCrossAdd(btn) {
      const card = btn.closest('[data-axd-card]');
      const select = card ? card.querySelector('[data-axd-size]') : null;

      // Si el producto tiene tallas, exige elegir una.
      let id = btn.dataset.add;
      if (select) {
        id = select.value;
        if (!id) {
          select.classList.add('is-error');
          select.focus();
          this.showError('Elige tu talla para agregar el producto.');
          return;
        }
        select.classList.remove('is-error');
      }
      if (!id) return;

      const label = btn.textContent;
      btn.disabled = true;
      this.addVariant(id, 1).then((ok) => {
        if (ok) {
          btn.classList.add('is-done');
          btn.textContent = 'Agregado ✓';
          if (select) select.selectedIndex = 0;
          setTimeout(() => { btn.classList.remove('is-done'); btn.textContent = label; btn.disabled = false; }, 1600);
        } else {
          btn.disabled = false;
        }
      });
    }

    /* ---- Render ---- */
    render(cart) {
      const empty = !cart || cart.item_count === 0;

      updateHeaderBadge(cart ? cart.item_count : 0);
      if (this.countEl) this.countEl.textContent = cart ? cart.item_count : 0;
      if (this.countEl) this.countEl.hidden = empty;

      // Items
      if (this.itemsEl) {
        this.itemsEl.innerHTML = empty ? '' : cart.items.map((it, i) => this.itemHTML(it, i + 1)).join('');
        this.itemsEl.hidden = empty;
      }
      if (this.emptyEl) this.emptyEl.hidden = !empty;
      if (this.footerEl) this.footerEl.hidden = empty;
      if (this.shipEl) this.shipEl.hidden = empty;
      if (this.promoEl) this.promoEl.hidden = empty;

      // Subtotal
      if (this.subtotalEl && cart) this.subtotalEl.textContent = money(cart.total_price);

      // Barra de envío gratis
      if (!empty) this.renderShipping(cart.total_price);

      // Promo por volumen (10% x2 / 15% x3 pares)
      if (!empty) this.renderPromo(cart.item_count);
    }

    renderPromo(count) {
      let nudge;
      let reached = false;
      if (count <= 1) {
        nudge = 'Suma otro par y obtén 10% OFF';
      } else if (count === 2) {
        nudge = '¡2 pares = 10% OFF! Suma 1 más para 15%';
        reached = true;
      } else {
        nudge = '¡Llevas 3+ pares: 15% OFF!';
        reached = true;
      }
      if (this.promoEl) this.promoEl.classList.toggle('is-reached', reached);
      if (this.promoNudgeEl) this.promoNudgeEl.textContent = nudge;
    }

    itemHTML(item, line) {
      const variant =
        item.options_with_values && item.options_with_values.length && !item.product_has_only_default_variant
          ? item.options_with_values.map((o) => `${o.name}: ${o.value}`).join(' · ')
          : (item.variant_title || '');
      const compare =
        item.original_line_price > item.final_line_price
          ? `<s>${money(item.original_line_price)}</s>`
          : '';
      const img = item.image
        ? `<img src="${item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" loading="lazy" width="72" height="72">`
        : '';
      return `
        <div class="axd__item" data-line="${line}">
          <a class="axd__item-media" href="${item.url}">${img}</a>
          <div class="axd__item-main">
            <div class="axd__item-top">
              <a class="axd__item-name" href="${item.url}">${item.product_title || item.title}</a>
              <span class="axd__item-price">${money(item.final_line_price)}${compare}</span>
            </div>
            ${variant ? `<p class="axd__item-variant">${variant}</p>` : ''}
            <div class="axd__item-bottom">
              <div class="axd__stepper" role="group" aria-label="Cantidad">
                <button class="axd__step" type="button" data-axd-step data-line="${line}" data-next="${item.quantity - 1}" aria-label="Quitar uno"${item.quantity <= 1 ? '' : ''}>−</button>
                <span class="axd__qty" aria-live="polite">${item.quantity}</span>
                <button class="axd__step" type="button" data-axd-step data-line="${line}" data-next="${item.quantity + 1}" aria-label="Agregar uno">+</button>
              </div>
              <button class="axd__remove" type="button" data-axd-remove data-line="${line}">Quitar</button>
            </div>
          </div>
        </div>`;
    }

    renderShipping(total) {
      const reached = total >= THRESHOLD;
      const remaining = Math.max(0, THRESHOLD - total);
      const frac = Math.min(1, THRESHOLD ? total / THRESHOLD : 0);

      let msg;
      if (reached) {
        msg = '¡Tienes envío gratis! 🎉';
      } else {
        msg = `Te faltan <b>${money(remaining)}</b> para envío gratis`;
      }

      const cartIcon =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2 3h2.2l2 12.3a1.6 1.6 0 0 0 1.6 1.4h8.7a1.6 1.6 0 0 0 1.6-1.3L21 7H5.2"/></svg>';

      this.shipEl.classList.toggle('is-reached', reached);
      this.shipEl.innerHTML = `
        <p class="axd__ship-msg">${msg}</p>
        <div class="axd__ship-track">
          <span class="axd__ship-fill"></span>
          <span class="axd__ship-marker">${cartIcon}</span>
        </div>`;

      const fill = this.shipEl.querySelector('.axd__ship-fill');
      const track = this.shipEl.querySelector('.axd__ship-track');
      const marker = this.shipEl.querySelector('.axd__ship-marker');
      // Animamos con transform (no layout): fill = scaleX, marker = translateX en px.
      requestAnimationFrame(() => {
        fill.style.transform = `scaleX(${frac})`;
        // El marcador (left:-13px) recorre de 0 al ancho de la barra siguiendo el borde del fill.
        marker.style.transform = `translateX(${track.clientWidth * frac}px) translateY(-50%)`;
      });
    }

    /* ---- Estado / errores ---- */
    setBusy(state) {
      this.busy = state;
      if (this.panel) this.panel.classList.toggle('is-loading', state);
    }

    showError(message) {
      if (!this.errorEl) { window.alert(message); return; }
      this.errorEl.innerHTML = message;
      this.errorEl.hidden = false;
      requestAnimationFrame(() => this.errorEl.classList.add('is-visible'));
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => {
        this.errorEl.classList.remove('is-visible');
        setTimeout(() => { this.errorEl.hidden = true; }, 240);
      }, 3200);
    }
  }

  if (!customElements.get('alturex-cart-drawer')) {
    customElements.define('alturex-cart-drawer', AlturexCartDrawer);
  }
})();
